import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { CheckCircleOutlined, PlusOutlined, RollbackOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  financeApi,
  type Expense,
  type ExpenseInput,
  type ExpensePaymentType,
} from '@/api/finance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormShape {
  expenseDate: Dayjs;
  expenseCategoryId: number;
  amount: number;
  description: string;
  paymentType: ExpensePaymentType;
  bankAccountId: number;
  glAccountId: number;
}

export function FinanceExpensesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FINANCE:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FINANCE:EDIT'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<Expense | null>(null);
  const [approving, setApproving] = useState<Expense | null>(null);
  const [approveGlAccountId, setApproveGlAccountId] = useState<number | undefined>();
  const [form] = Form.useForm<FormShape>();
  const [reverseReason, setReverseReason] = useState('');
  const canApprove = useAuthStore((s) => s.hasAuthority('FINANCE:APPROVE'));

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['finance', 'expenses', params],
    queryFn: () => financeApi.listExpenses(params.from, params.to),
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
    staleTime: 60_000,
  });

  const { data: accounts } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: financeApi.listAccounts,
    staleTime: 60_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['setup', 'lookups', 'expense-categories', 'active'],
    queryFn: () => setupApi.listLookup('expense-categories', true),
    staleTime: 5 * 60_000,
  });

  const add = useMutation({
    mutationFn: (values: FormShape) => {
      const body: ExpenseInput = {
        expenseDate: values.expenseDate.format('YYYY-MM-DD'),
        expenseCategoryId: values.expenseCategoryId,
        amount: values.amount.toFixed(2),
        description: values.description.trim(),
        paymentType: values.paymentType,
        bankAccountId: values.bankAccountId,
        glAccountId: values.glAccountId,
      };
      return financeApi.addExpense(body);
    },
    onSuccess: () => {
      message.success('Expense recorded and posted to ledger');
      qc.invalidateQueries({ queryKey: ['finance'] });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      financeApi.reverseExpense(id, reason),
    onSuccess: () => {
      message.success('Expense reversed');
      qc.invalidateQueries({ queryKey: ['finance'] });
      setReversing(null);
      setReverseReason('');
    },
    onError: (e) => message.error(extractError(e)),
  });

  const approve = useMutation({
    mutationFn: ({ id, glAccountId }: { id: number; glAccountId: number }) =>
      financeApi.approveExpense(id, glAccountId),
    onSuccess: () => {
      message.success('Expense approved and posted');
      qc.invalidateQueries({ queryKey: ['finance'] });
      setApproving(null);
      setApproveGlAccountId(undefined);
    },
    onError: (e) => message.error(extractError(e, 'Approval failed')),
  });

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({
      expenseDate: dayjs(),
      paymentType: 'CASH',
    });
    setAdding(true);
  };

  const watchedPayment = Form.useWatch('paymentType', form);
  const bankOptions = (bankAccounts ?? [])
    .filter((b) =>
      watchedPayment === 'CASH' ? b.cash : watchedPayment === 'BANK' ? !b.cash : false,
    )
    .map((b) => ({ value: b.id, label: b.accountName + (b.bankName ? ` · ${b.bankName}` : '') }));

  const expenseAccountOptions = (accounts ?? [])
    .filter((a) => a.accountType === 'EXPENSE' && a.postingAccount && a.active)
    .map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }));

  const totalAmount = (data ?? [])
    .filter((e) => !e.reversed)
    .reduce((acc, e) => acc + Number(e.amount), 0);

  const columns: TableProps<Expense>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'expenseDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Category',
      dataIndex: 'expenseCategoryName',
      width: 180,
      render: (n: string | null) => n ?? '—',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (d: string, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text delete={row.reversed}>{d}</Typography.Text>
          {row.glAccountName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              GL: {row.glAccountName}
            </Typography.Text>
          )}
          {row.requiresApproval && !row.approved && !row.reversed && (
            <Tag color="orange">Pending approval</Tag>
          )}
          {row.approved && (
            <Tag color="green">Approved</Tag>
          )}
          {row.reversed && (
            <Tag color="default">Reversed{row.reversalReason ? ` · ${row.reversalReason}` : ''}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Method',
      width: 130,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Tag color={row.paymentType === 'CASH' ? 'success' : row.paymentType === 'BANK' ? 'processing' : 'default'}>
            {row.paymentType}
          </Tag>
          {row.bankAccountName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.bankAccountName}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 140,
      align: 'right',
      render: (a: string, row) => (
        <Typography.Text strong delete={row.reversed}>PKR {PKR(a)}</Typography.Text>
      ),
    },
    {
      title: 'Actions',
      width: 170,
      align: 'right',
      render: (_, row) => {
        if (row.reversed) return null;
        if (row.requiresApproval && !row.approved) {
          return canApprove ? (
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setApproving(row)}
            >
              Approve
            </Button>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              awaiting approver
            </Typography.Text>
          );
        }
        return canEdit ? (
          <Button
            size="small"
            type="text"
            icon={<RollbackOutlined />}
            onClick={() => setReversing(row)}
          >
            Reverse
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Expenses"
        description="Recorded expenses with auto-posting to the ledger."
        count={data ? { value: data.length, label: 'in range' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              New expense
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && data.length > 0 && (
          <Tag color="processing" style={{ fontSize: 13 }}>
            Total · PKR {PKR(totalAmount)}
          </Tag>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No expenses in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </Card>
      )}

      <Modal
        title="Record expense"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
        okText="Record"
        destroyOnHidden
        width={560}
      >
        {adding && (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if (changed.paymentType !== undefined) form.setFieldValue('bankAccountId', undefined);
            }}
            onFinish={(values) => add.mutate(values)}
          >
            <Form.Item label="Date" name="expenseDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item label="Category" name="expenseCategoryId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <Form.Item label="Description" name="description" rules={[{ required: true, max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
            <Form.Item label="Amount (PKR)" name="amount" rules={[{ required: true, type: 'number', min: 0.01 }]}>
              <InputNumber style={{ width: '100%' }} step={100} min={0} />
            </Form.Item>
            <Form.Item label="GL expense account" name="glAccountId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={expenseAccountOptions}
                placeholder="Pick an EXPENSE posting account"
              />
            </Form.Item>
            <Form.Item label="Method" name="paymentType" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'CASH', label: 'Cash' },
                  { value: 'BANK', label: 'Bank' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Pay from" name="bankAccountId" rules={[{ required: true }]}>
              <Select options={bankOptions} placeholder="Pick an account" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={approving ? `Approve · PKR ${PKR(approving.amount)} — ${approving.description}` : 'Approve expense'}
        open={approving != null}
        onCancel={() => {
          setApproving(null);
          setApproveGlAccountId(undefined);
        }}
        okText="Approve & post"
        okButtonProps={{ disabled: !approveGlAccountId || approve.isPending }}
        confirmLoading={approve.isPending}
        onOk={() =>
          approving && approveGlAccountId && approve.mutate({ id: approving.id, glAccountId: approveGlAccountId })
        }
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          Approving posts to the GL — the chosen expense account is debited and the bank/cash
          account credited. This action cannot be undone (it can only be reversed).
        </Typography.Paragraph>
        <label style={{ fontWeight: 500 }}>GL expense account</label>
        <Select
          autoFocus
          style={{ width: '100%', marginTop: 6 }}
          showSearch
          optionFilterProp="label"
          value={approveGlAccountId}
          onChange={(v) => setApproveGlAccountId(v)}
          options={expenseAccountOptions}
          placeholder="Pick an EXPENSE posting account"
        />
      </Modal>

      <Modal
        title={reversing ? `Reverse expense · PKR ${PKR(reversing.amount)}` : 'Reverse expense'}
        open={reversing != null}
        onCancel={() => {
          setReversing(null);
          setReverseReason('');
        }}
        okText="Reverse"
        okButtonProps={{ danger: true, disabled: !reverseReason.trim() || reverse.isPending }}
        confirmLoading={reverse.isPending}
        onOk={() => reversing && reverse.mutate({ id: reversing.id, reason: reverseReason.trim() })}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          The original ledger entries are preserved. A compensating pair is posted to neutralize the impact.
        </Typography.Paragraph>
        <label style={{ fontWeight: 500 }}>Reason (required)</label>
        <Input.TextArea
          autoFocus
          autoSize={{ minRows: 2, maxRows: 4 }}
          value={reverseReason}
          onChange={(e) => setReverseReason(e.target.value)}
          style={{ marginTop: 6 }}
        />
      </Modal>
    </div>
  );
}
